import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  Settings, 
  Link2, 
  Unlink, 
  RefreshCw, 
  CheckCircle2, 
  XCircle,
  Building2,
  CreditCard,
  Banknote,
  Receipt,
  Percent,
  Tag,
  AlertCircle
} from 'lucide-react';

const AccountingSettingsPage = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [businesses, setBusinesses] = useState([]);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [settings, setSettings] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [taxRates, setTaxRates] = useState([]);
  const [trackingCategories, setTrackingCategories] = useState([]);
  const [loyverseStores, setLoyverseStores] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [fetchingAccounts, setFetchingAccounts] = useState(false);
  
  // Organization selection state (for Zoho with multiple orgs)
  const [showOrgSelector, setShowOrgSelector] = useState(false);
  const [pendingOrganizations, setPendingOrganizations] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState('');
  const [selectingOrg, setSelectingOrg] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    platform: '',
    line_item_description: 'Daily Sales - {branch_name} - {date}',
    include_branch_tag: true,
    account_mapping: {
      sales_account_id: '',
      sales_account_name: '',
      sales_account_code: '',
      cash_account_id: '',
      cash_account_name: '',
      cash_account_code: '',
      card_account_id: '',
      card_account_name: '',
      card_account_code: '',
      eft_account_id: '',
      eft_account_name: '',
      eft_account_code: '',
      cash_tips_account_id: '',
      cash_tips_account_name: '',
      cash_tips_account_code: '',
      card_tips_account_id: '',
      card_tips_account_name: '',
      card_tips_account_code: '',
      refunds_account_id: '',
      refunds_account_name: '',
      refunds_account_code: '',
      vat_rate_id: '',
      vat_rate_name: '',
      vat_percentage: 15,
      tracking_category_id: '',
      tracking_category_name: '',
      customer_id: '',
      customer_name: '',
      branch_mappings: {}
    }
  });

  useEffect(() => {
    fetchBusinesses();
  }, []);

  useEffect(() => {
    if (selectedBusiness) {
      fetchSettings(selectedBusiness);
    }
  }, [selectedBusiness]);

  const fetchBusinesses = async () => {
    try {
      const res = await api.get('/api-keys');
      setBusinesses(res.data.filter(b => b.is_active));
      if (res.data.length > 0) {
        setSelectedBusiness(res.data[0].id);
      }
    } catch (error) {
      toast.error('Failed to load businesses');
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async (businessId) => {
    try {
      const res = await api.get(`/admin/accounting-settings/${businessId}`);
      setSettings(res.data);
      const savedMapping = res.data.account_mapping || {};
      setFormData({
        platform: res.data.platform || '',
        line_item_description: res.data.line_item_description || 'Daily Sales - {branch_name} - {date}',
        include_branch_tag: res.data.include_branch_tag !== false,
        account_mapping: {
          ...formData.account_mapping,
          ...savedMapping,
          branch_mappings: savedMapping.branch_mappings || {}
        }
      });
      
      // If connected, fetch accounts
      if (res.data.is_connected) {
        fetchAccounts(businessId);
      }
    } catch (error) {
      toast.error('Failed to load settings');
    }
  };

  const fetchAccounts = async (businessId) => {
    setFetchingAccounts(true);
    try {
      const res = await api.get(`/admin/accounting/accounts/${businessId}`);
      setAccounts(res.data.accounts || []);
      setTaxRates(res.data.tax_rates || []);
      setTrackingCategories(res.data.tracking_categories || []);
      setLoyverseStores(res.data.loyverse_stores || []);
      setCustomers(res.data.customers || []);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Connection expired. Please reconnect.');
        setSettings(prev => ({ ...prev, is_connected: false }));
      } else {
        toast.error('Failed to fetch accounts');
      }
    } finally {
      setFetchingAccounts(false);
    }
  };

  const handleConnect = async () => {
    if (!formData.platform) {
      toast.error('Please select an accounting platform first');
      return;
    }

    setConnecting(true);
    try {
      // First save the platform selection
      await api.post('/admin/accounting-settings', {
        business_id: selectedBusiness,
        platform: formData.platform,
        line_item_description: formData.line_item_description,
        include_branch_tag: formData.include_branch_tag,
        account_mapping: formData.account_mapping
      });

      // Get OAuth URL
      const res = await api.get(`/admin/accounting/oauth-url/${selectedBusiness}?platform=${formData.platform}`);
      
      // Open OAuth window
      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      
      const popup = window.open(
        res.data.url,
        'oauth',
        `width=${width},height=${height},left=${left},top=${top}`
      );

      // Listen for OAuth callback
      const handleMessage = async (event) => {
        if (event.data.type === 'oauth_callback') {
          window.removeEventListener('message', handleMessage);
          popup?.close();
          
          try {
            console.log('OAuth callback received:', event.data);
            const callbackRes = await api.post(`/admin/accounting/oauth-callback?code=${encodeURIComponent(event.data.code)}&state=${encodeURIComponent(event.data.state)}`);
            
            // Check if organization selection is required (Zoho with multiple orgs)
            if (callbackRes.data.requires_org_selection) {
              setPendingOrganizations(callbackRes.data.organizations);
              setShowOrgSelector(true);
              setConnecting(false);
              return;
            }
            
            toast.success(`Connected to ${callbackRes.data.organization_name}`);
            fetchSettings(selectedBusiness);
          } catch (error) {
            console.error('OAuth callback error:', error.response?.data || error);
            toast.error(error.response?.data?.detail || 'Failed to complete connection');
          }
          setConnecting(false);
        }
      };
      
      window.addEventListener('message', handleMessage);
      
      // Timeout after 5 minutes
      setTimeout(() => {
        window.removeEventListener('message', handleMessage);
        setConnecting(false);
      }, 300000);
      
    } catch (error) {
      toast.error('Failed to initiate connection');
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      await api.post(`/admin/accounting/disconnect/${selectedBusiness}`);
      toast.success('Disconnected successfully');
      setSettings(prev => ({ ...prev, is_connected: false, organization_name: null }));
      setAccounts([]);
      setTaxRates([]);
      setTrackingCategories([]);
    } catch (error) {
      toast.error('Failed to disconnect');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.post('/admin/accounting-settings', {
        business_id: selectedBusiness,
        platform: formData.platform,
        line_item_description: formData.line_item_description,
        include_branch_tag: formData.include_branch_tag,
        account_mapping: formData.account_mapping
      });
      toast.success('Settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleAccountSelect = (field, accountId) => {
    const account = accounts.find(a => a.id === accountId);
    setFormData(prev => ({
      ...prev,
      account_mapping: {
        ...prev.account_mapping,
        [field]: accountId,
        [field.replace('_id', '_name')]: account?.name || '',
        [field.replace('_id', '_code')]: account?.code || ''
      }
    }));
  };

  const handleTaxSelect = (taxId) => {
    const tax = taxRates.find(t => t.id === taxId);
    setFormData(prev => ({
      ...prev,
      account_mapping: {
        ...prev.account_mapping,
        vat_rate_id: taxId,
        vat_rate_name: tax?.name || '',
        vat_percentage: tax?.percentage || 15
      }
    }));
  };

  const handleOrganizationSelect = async () => {
    if (!selectedOrganization) {
      toast.error('Please select an organization');
      return;
    }
    
    setSelectingOrg(true);
    try {
      const res = await api.post(`/admin/accounting/select-organization?business_id=${selectedBusiness}&organization_id=${selectedOrganization}`);
      toast.success(`Connected to ${res.data.organization_name}`);
      setShowOrgSelector(false);
      setPendingOrganizations([]);
      setSelectedOrganization('');
      fetchSettings(selectedBusiness);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete connection');
    } finally {
      setSelectingOrg(false);
    }
  };

  const getBusinessName = () => {
    return businesses.find(b => b.id === selectedBusiness)?.business_name || 'Select Business';
  };

  const getPlatformLabel = (platform) => {
    if (platform === 'zoho') return 'Zoho Books';
    if (platform === 'xero') return 'Xero';
    return platform;
  };

  // Filter accounts by type for dropdowns
  const incomeAccounts = accounts.filter(a => 
    a.type?.toLowerCase().includes('income') || 
    a.type?.toLowerCase().includes('revenue') ||
    a.type?.toLowerCase().includes('sales')
  );
  
  const bankAccounts = accounts.filter(a => 
    a.type?.toLowerCase().includes('bank') || 
    a.type?.toLowerCase().includes('cash')
  );
  
  // For card payments - include bank, liability, current assets (clearing accounts)
  const cardPaymentAccounts = accounts.filter(a => 
    a.type?.toLowerCase().includes('bank') || 
    a.type?.toLowerCase().includes('current') ||
    a.type?.toLowerCase().includes('liability') ||
    a.type?.toLowerCase().includes('clearing') ||
    a.type?.toLowerCase().includes('asset') ||
    a.type?.toLowerCase().includes('receivable') ||
    a.name?.toLowerCase().includes('eft') ||
    a.name?.toLowerCase().includes('clearing') ||
    a.name?.toLowerCase().includes('loyverse')
  );
  
  const liabilityAccounts = accounts.filter(a => 
    a.type?.toLowerCase().includes('liability') || 
    a.type?.toLowerCase().includes('payable') ||
    a.type?.toLowerCase().includes('control')
  );

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-48"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[#1A1A18]">Accounting Integration</h1>
        <p className="text-muted-foreground mt-1">
          Connect your accounting software to automatically create invoices when shifts are reconciled
        </p>
      </div>

      {/* Business Selector */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <Building2 className="h-5 w-5 text-muted-foreground" />
            <div className="flex-1">
              <Label>Select Business</Label>
              <Select value={selectedBusiness || ''} onValueChange={setSelectedBusiness}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select a business" />
                </SelectTrigger>
                <SelectContent>
                  {businesses.map(b => (
                    <SelectItem key={b.id} value={b.id}>
                      {b.business_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {selectedBusiness && (
        <>
          {/* Connection Status */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Link2 className="h-5 w-5" />
                Connection Status
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Accounting Platform</Label>
                  <Select 
                    value={formData.platform} 
                    onValueChange={(v) => setFormData(prev => ({ ...prev, platform: v }))}
                    disabled={settings?.is_connected}
                  >
                    <SelectTrigger className="w-48 mt-1">
                      <SelectValue placeholder="Select platform" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="zoho">Zoho Books</SelectItem>
                      <SelectItem value="xero">Xero</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="text-right">
                  {settings?.is_connected ? (
                    <div className="space-y-2">
                      <Badge className="bg-green-100 text-green-800 border-green-200">
                        <CheckCircle2 className="h-3 w-3 mr-1" />
                        Connected
                      </Badge>
                      <p className="text-sm text-muted-foreground">
                        {settings.organization_name}
                      </p>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={handleDisconnect}
                        className="text-red-600"
                      >
                        <Unlink className="h-4 w-4 mr-1" />
                        Disconnect
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Badge variant="outline" className="bg-gray-100">
                        <XCircle className="h-3 w-3 mr-1" />
                        Not Connected
                      </Badge>
                      <div>
                        <Button 
                          onClick={handleConnect}
                          disabled={!formData.platform || connecting}
                        >
                          {connecting ? (
                            <>
                              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                              Connecting...
                            </>
                          ) : (
                            <>
                              <Link2 className="h-4 w-4 mr-2" />
                              Connect to {getPlatformLabel(formData.platform) || 'Platform'}
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Account Mapping - Only show if connected */}
          {settings?.is_connected && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Settings className="h-5 w-5" />
                      Account Mapping
                    </CardTitle>
                    <CardDescription>
                      Map your accounts for automatic invoice creation
                    </CardDescription>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fetchAccounts(selectedBusiness)}
                    disabled={fetchingAccounts}
                  >
                    <RefreshCw className={`h-4 w-4 mr-2 ${fetchingAccounts ? 'animate-spin' : ''}`} />
                    Refresh Accounts
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {accounts.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>No accounts loaded. Click &quot;Refresh Accounts&quot; to fetch from {getPlatformLabel(formData.platform)}.</p>
                  </div>
                ) : (
                  <>
                    {/* Sales & Revenue */}
                    <div className="space-y-4">
                      <h3 className="font-medium flex items-center gap-2">
                        <Receipt className="h-4 w-4" />
                        Sales & Revenue
                      </h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Sales Account (with VAT)</Label>
                          <Select 
                            value={formData.account_mapping.sales_account_id || ''} 
                            onValueChange={(v) => handleAccountSelect('sales_account_id', v)}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Select account" />
                            </SelectTrigger>
                            <SelectContent>
                              {incomeAccounts.map(a => (
                                <SelectItem key={a.id} value={a.id}>
                                  {a.code ? `${a.code} - ` : ''}{a.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Refunds Account</Label>
                          <Select 
                            value={formData.account_mapping.refunds_account_id || ''} 
                            onValueChange={(v) => handleAccountSelect('refunds_account_id', v)}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Select account" />
                            </SelectTrigger>
                            <SelectContent>
                              {incomeAccounts.map(a => (
                                <SelectItem key={a.id} value={a.id}>
                                  {a.code ? `${a.code} - ` : ''}{a.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    {/* Payment Accounts */}
                    <div className="space-y-4">
                      <h3 className="font-medium flex items-center gap-2">
                        <CreditCard className="h-4 w-4" />
                        Payment Accounts
                      </h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Cash Account</Label>
                          <Select 
                            value={formData.account_mapping.cash_account_id || ''} 
                            onValueChange={(v) => handleAccountSelect('cash_account_id', v)}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Select account" />
                            </SelectTrigger>
                            <SelectContent>
                              {bankAccounts.map(a => (
                                <SelectItem key={a.id} value={a.id}>
                                  {a.code ? `${a.code} - ` : ''}{a.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Card Account</Label>
                          <Select 
                            value={formData.account_mapping.card_account_id || ''} 
                            onValueChange={(v) => handleAccountSelect('card_account_id', v)}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Select account" />
                            </SelectTrigger>
                            <SelectContent>
                              {cardPaymentAccounts.map(a => (
                                <SelectItem key={a.id} value={a.id}>
                                  {a.code ? `${a.code} - ` : ''}{a.name} <span className="text-muted-foreground text-xs">({a.type})</span>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>EFT Clearing Account</Label>
                          <Select 
                            value={formData.account_mapping.eft_account_id || ''} 
                            onValueChange={(v) => handleAccountSelect('eft_account_id', v)}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Select account" />
                            </SelectTrigger>
                            <SelectContent>
                              {cardPaymentAccounts.map(a => (
                                <SelectItem key={a.id} value={a.id}>
                                  {a.code ? `${a.code} - ` : ''}{a.name} <span className="text-muted-foreground text-xs">({a.type})</span>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    {/* Tips Control Accounts */}
                    <div className="space-y-4">
                      <h3 className="font-medium flex items-center gap-2">
                        <Banknote className="h-4 w-4" />
                        Tips Control Accounts (No VAT)
                      </h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Cash Tips Control Account</Label>
                          <Select 
                            value={formData.account_mapping.cash_tips_account_id || ''} 
                            onValueChange={(v) => handleAccountSelect('cash_tips_account_id', v)}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Select account" />
                            </SelectTrigger>
                            <SelectContent>
                              {[...liabilityAccounts, ...accounts].map(a => (
                                <SelectItem key={a.id} value={a.id}>
                                  {a.code ? `${a.code} - ` : ''}{a.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Card Tips Control Account</Label>
                          <Select 
                            value={formData.account_mapping.card_tips_account_id || ''} 
                            onValueChange={(v) => handleAccountSelect('card_tips_account_id', v)}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Select account" />
                            </SelectTrigger>
                            <SelectContent>
                              {[...liabilityAccounts, ...accounts].map(a => (
                                <SelectItem key={a.id} value={a.id}>
                                  {a.code ? `${a.code} - ` : ''}{a.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    {/* VAT Settings */}
                    <div className="space-y-4">
                      <h3 className="font-medium flex items-center gap-2">
                        <Percent className="h-4 w-4" />
                        VAT / Tax Settings
                      </h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>VAT Rate</Label>
                          <Select 
                            value={formData.account_mapping.vat_rate_id || ''} 
                            onValueChange={handleTaxSelect}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Select VAT rate" />
                            </SelectTrigger>
                            <SelectContent>
                              {taxRates.map(t => (
                                <SelectItem key={t.id} value={t.id}>
                                  {t.name} ({t.percentage}%)
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>

                    {/* Customer Selection (Zoho only) */}
                    {formData.platform === 'zoho' && (
                      <>
                        <Separator />
                        <div className="space-y-4">
                          <h3 className="font-medium flex items-center gap-2">
                            <Building2 className="h-4 w-4" />
                            Customer / Contact
                          </h3>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label>Invoice Customer</Label>
                              <Select 
                                value={formData.account_mapping.customer_id || ''} 
                                onValueChange={(v) => {
                                  const customer = customers.find(c => c.id === v);
                                  setFormData(prev => ({
                                    ...prev,
                                    account_mapping: {
                                      ...prev.account_mapping,
                                      customer_id: v,
                                      customer_name: customer?.name || ''
                                    }
                                  }));
                                }}
                              >
                                <SelectTrigger className="mt-1">
                                  <SelectValue placeholder="Select customer" />
                                </SelectTrigger>
                                <SelectContent>
                                  {customers.map(c => (
                                    <SelectItem key={c.id} value={c.id}>
                                      {c.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <p className="text-xs text-muted-foreground mt-1">
                                Select the Zoho customer for daily sales invoices (e.g., &quot;Loyverse Sales&quot;)
                              </p>
                            </div>
                          </div>
                          {customers.length === 0 && (
                            <p className="text-sm text-amber-600">
                              No customers found. Click &quot;Refresh Accounts&quot; to load them, or create a customer in Zoho Books first.
                            </p>
                          )}
                        </div>
                      </>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {/* Invoice Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Tag className="h-5 w-5" />
                Invoice Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Line Item Description Template</Label>
                <Input
                  value={formData.line_item_description}
                  onChange={(e) => setFormData(prev => ({ ...prev, line_item_description: e.target.value }))}
                  placeholder="Daily Sales - {branch_name} - {date}"
                  className="mt-1"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Available placeholders: {'{branch_name}'}, {'{date}'}, {'{shift_id}'}
                </p>
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <Label>Include Branch as Tag/Tracking</Label>
                  <p className="text-xs text-muted-foreground">
                    Add branch name as a tag or tracking category on invoices
                  </p>
                </div>
                <Switch
                  checked={formData.include_branch_tag}
                  onCheckedChange={(v) => setFormData(prev => ({ ...prev, include_branch_tag: v }))}
                />
              </div>
              
              {formData.include_branch_tag && trackingCategories.length > 0 && loyverseStores.length > 0 && (
                <div className="space-y-4">
                  <div>
                    <Label>Branch Mappings</Label>
                    <p className="text-xs text-muted-foreground mb-3">
                      Map each Loyverse store to its corresponding {formData.platform === 'xero' ? 'tracking option' : 'reporting tag'}
                    </p>
                  </div>
                  
                  <div className="space-y-3">
                    {loyverseStores.map(store => {
                      // Get the saved mapping for this store
                      const savedMapping = formData.account_mapping.branch_mappings?.[store.id];
                      // Find the matching tracking category by comparing tag_id and tag_option_id
                      const savedValue = savedMapping 
                        ? `${savedMapping.tag_id}:${savedMapping.tag_option_id}`
                        : 'none';
                      
                      return (
                      <div key={store.id} className="flex items-center gap-4 p-3 border rounded-lg bg-muted/30">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{store.name}</p>
                          <p className="text-xs text-muted-foreground">Loyverse Store</p>
                        </div>
                        <div className="text-muted-foreground">→</div>
                        <div className="flex-1">
                          <Select 
                            value={savedValue} 
                            onValueChange={(v) => {
                              if (v === 'none') {
                                setFormData(prev => ({
                                  ...prev,
                                  account_mapping: {
                                    ...prev.account_mapping,
                                    branch_mappings: {
                                      ...(prev.account_mapping.branch_mappings || {}),
                                      [store.id]: null
                                    }
                                  }
                                }));
                                return;
                              }
                              const tc = trackingCategories.find(t => t.id === v);
                              setFormData(prev => ({
                                ...prev,
                                account_mapping: {
                                  ...prev.account_mapping,
                                  branch_mappings: {
                                    ...(prev.account_mapping.branch_mappings || {}),
                                    [store.id]: tc ? {
                                      tag_id: tc.tag_id,  // The actual Zoho tag_id
                                      tag_name: tc.name,
                                      tag_option_id: tc.tag_option_id || tc.option_id,
                                      option_name: tc.tag_option_name || tc.option_name,
                                      tracking_category_id: tc.tracking_category_id || tc.tag_id
                                    } : null
                                  }
                                }
                              }));
                            }}
                          >
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder="Select tag" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">-- Not mapped --</SelectItem>
                              {trackingCategories.map(tc => (
                                <SelectItem key={tc.id} value={tc.id}>
                                  {tc.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    );
                    })}
                  </div>
                </div>
              )}
              
              {formData.include_branch_tag && trackingCategories.length === 0 && settings?.is_connected && (
                <p className="text-sm text-muted-foreground">
                  No {formData.platform === 'xero' ? 'tracking categories' : 'reporting tags'} found. 
                  Click &quot;Refresh Accounts&quot; to load them.
                </p>
              )}
              
              {formData.include_branch_tag && loyverseStores.length === 0 && settings?.is_connected && (
                <p className="text-sm text-muted-foreground">
                  No Loyverse stores found. Click &quot;Refresh Accounts&quot; to load them.
                </p>
              )}
            </CardContent>
          </Card>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save Settings'}
            </Button>
          </div>
        </>
      )}

      {/* Organization Selector Dialog (for Zoho with multiple orgs) */}
      <Dialog open={showOrgSelector} onOpenChange={setShowOrgSelector}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Select Zoho Organization</DialogTitle>
            <DialogDescription>
              Your Zoho account has multiple organizations. Please select which one to connect.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>Organization</Label>
            <Select value={selectedOrganization} onValueChange={setSelectedOrganization}>
              <SelectTrigger className="mt-2">
                <SelectValue placeholder="Select an organization" />
              </SelectTrigger>
              <SelectContent>
                {pendingOrganizations.map(org => (
                  <SelectItem key={org.id} value={org.id}>
                    {org.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowOrgSelector(false);
              setPendingOrganizations([]);
              setSelectedOrganization('');
            }}>
              Cancel
            </Button>
            <Button onClick={handleOrganizationSelect} disabled={selectingOrg || !selectedOrganization}>
              {selectingOrg ? 'Connecting...' : 'Connect'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AccountingSettingsPage;
